import { Check } from 'lucide-react'
import type { CSSProperties } from 'react'
import { Link } from 'react-router'
import { albumPhotoUrl, photoName, photoSrcSet } from '@/lib/albums'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'
import { usePhotoLink } from '@/lib/photoMotion'

export type GridLayout = 'rows' | 'squares'

// How wide a photo shows in a row of the given height (in rem), with room for the row stretching it to fit.
const shown = (ratio: number, rowHeight: number) => `${Math.round(ratio * rowHeight * 1.3 * 10) / 10}rem`

// Photos laid out to open. In rows, each keeps its own shape and the photos in a row share one height, like a
// printed album page. In squares they sit edge to edge, the way a phone shows a camera roll; a square crops the
// thumbnail, never the photo itself. With onToggle they're picked instead, for organizing: a picked photo draws in
// and shows a check.
export function PhotoGrid({
  photos,
  selected,
  onToggle,
  small,
  layout = 'squares',
}: {
  photos: { id: string; ref: string; width: number; height: number; caption: string; takenOn: string | null }[]
  selected?: Set<string>
  onToggle?: (id: string) => void
  small?: boolean
  layout?: GridLayout
}) {
  const rows = layout === 'rows'
  const open = usePhotoLink()
  return (
    <ul
      className={cn(
        rows
          ? // The last row keeps its photos' height instead of stretching them across.
            'flex flex-wrap gap-1.5 after:grow-[1000] after:content-[""] sm:gap-2.5'
          : 'grid grid-cols-3 gap-1 sm:gap-1.5',
        !rows && (small ? 'sm:grid-cols-4 lg:grid-cols-6' : 'sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'),
      )}
    >
      {photos.map((photo, i) => {
        const name = photoName(photo, i + 1)
        const picked = selected?.has(photo.id) ?? false
        // Very tall or very wide photos are held to a sensible shape in a row.
        const ratio = Math.min(Math.max(photo.width / photo.height, 0.5), 2.5)
        const shape: CSSProperties | undefined = rows
          ? { flexGrow: ratio, flexBasis: `calc(${ratio} * var(--row-h))`, aspectRatio: String(ratio) }
          : undefined
        const image = (
          <img
            src={albumPhotoUrl(photo.ref, 'thumb')}
            srcSet={photoSrcSet(photo, ['thumb', 'medium'])}
            sizes={
              rows
                ? `(min-width: 1024px) ${shown(ratio, 14)}, (min-width: 640px) ${shown(ratio, 12)}, ${shown(ratio, 7.5)}`
                : small
                  ? '(min-width: 1024px) 17vw, (min-width: 640px) 25vw, 33vw'
                  : '(min-width: 1280px) 17vw, (min-width: 1024px) 20vw, (min-width: 640px) 25vw, 33vw'
            }
            alt={onToggle ? '' : name}
            loading="lazy"
            decoding="async"
            className={cn(
              'size-full object-cover transition-[scale,border-radius] duration-300 ease-settle',
              tint(i),
              onToggle ? picked && 'scale-[0.88] rounded-lg' : 'group-hover:scale-[1.03]',
            )}
          />
        )
        const tile = cn(
          'group block overflow-hidden outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
          rows
            ? 'absolute inset-0 rounded-xl sm:rounded-2xl'
            : 'relative aspect-square w-full rounded-md sm:rounded-lg',
        )
        return (
          <li key={photo.id} style={shape} className="relative [--row-h:7.5rem] sm:[--row-h:12rem] lg:[--row-h:14rem]">
            {onToggle ? (
              <button
                type="button"
                aria-pressed={picked}
                aria-label={name}
                onClick={() => onToggle(photo.id)}
                className={cn(tile, picked && 'bg-secondary')}
              >
                {image}
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute top-2 right-2 grid size-6 place-items-center rounded-full border-2 transition-colors',
                    picked
                      ? 'border-brand bg-brand text-brand-foreground'
                      : 'border-viewer-foreground bg-viewer/25 text-transparent',
                  )}
                >
                  <Check className="size-3.5" strokeWidth={3} />
                </span>
              </button>
            ) : (
              <Link {...open(photo.id)} className={tile}>
                {image}
              </Link>
            )}
          </li>
        )
      })}
    </ul>
  )
}
